import React, { useEffect, useState } from 'react';
import { FaTrash, FaPlus, FaSearch, FaBook, FaChevronLeft, FaGripVertical, FaArrowUp, FaArrowDown, FaList, FaTimes, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { FaPencil } from "react-icons/fa6";
import Select from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';
import ApiService from '../../models/ApiService';

// DnD Kit imports
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableShayariItem = ({ shayari, onEdit, onDelete, onToggleStatus }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: shayari._id || shayari.no.toString(),
        data: {
            type: 'shayari',
            shayari
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        zIndex: isDragging ? 999 : 'auto',
        position: 'relative'
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`bg-white rounded-lg shadow-md overflow-hidden border border-[--border-color-g] hover:shadow-lg transition-all duration-300 
                ${shayari.is_active === false ? 'opacity-60' : ''} 
                ${isDragging ? 'shadow-xl ring-2 ring-[--primary-btn-color] scale-105' : ''}
                cursor-move`}
        >
            <div className="p-4">
                {/* Drag Handle and Index */}
                <div className="flex justify-between items-center mb-3 pb-2">
                    <div className="flex items-center gap-3">
                        <div
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-2 hover:bg-[--bg-color] rounded-lg transition-colors"
                            title="Drag to reorder"
                        >
                            <FaGripVertical className="text-[--secondary-color] text-xl" />
                        </div>
                    </div>
                    <span className="text-sm text-gray-500">No: {shayari.no}</span>
                </div>

                {/* Urdu Content */}
                <div className="flex justify-end mb-4">
                    <p
                        className="text-lg text-[--text-color-g]"
                        id='primary-urdu-fonts'
                        dir='rtl'
                        dangerouslySetInnerHTML={{ __html: shayari.urdu?.replace(/\n/g, '<br />') || '' }}
                    />
                </div>

                {/* Poet Name in Urdu */}
                {shayari.poet_urdu && (
                    <div className="flex justify-end mb-4">
                        <p
                            className="text-sm font-semibold text-[--text-color-g]"
                            id='primary-urdu-fonts'
                            dir='rtl'
                            style={{ fontStyle: 'italic' }}
                        >
                            — {shayari.poet_urdu}
                        </p>
                    </div>
                )}

                {/* Devnagari Content */}
                {shayari.devnagari && (
                    <div className="mb-4 bg-gray-50 p-3 rounded">
                        <p className="text-base text-black"
                            dangerouslySetInnerHTML={{ __html: shayari.devnagari.replace(/\n/g, '<br />') }}
                        />
                    </div>
                )}

                {/* Poet Name Hindi */}
                {shayari.poet_hindi && (
                    <div className="flex justify-start mb-4">
                        <p
                            className="text-sm font-semibold text-[--text-color-g]"
                            style={{ fontStyle: 'italic' }}
                        >
                            {shayari.poet_hindi}
                        </p>
                    </div>
                )}

                {/* Roman Content */}
                {shayari.roman && (
                    <div className="mb-4 bg-gray-50 p-3 rounded">
                        <p className="text-base text-black"
                            dangerouslySetInnerHTML={{ __html: shayari.roman.replace(/\n/g, '<br />') }}
                        />
                    </div>
                )}

                {/* Footer with English Poet Name and Actions */}
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-[--border-color-g]">
                    <span className="text-sm font-semibold text-[--text-color-g]">{shayari.poet}</span>

                    <div className="flex items-center gap-2">
                        {/* Toggle Switch */}
                        <label className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded-lg transition-colors">
                            <input
                                type="checkbox"
                                checked={shayari.is_published}
                                onChange={() => onToggleStatus(shayari._id || shayari.no, shayari.is_published)}
                                className="hidden"
                            />
                            <div className={`w-8 h-4 flex items-center rounded-full p-0.5 transition-colors 
                                ${shayari.is_published ? 'bg-green-500' : 'bg-gray-400'}`}>
                                <div className={`bg-white w-3 h-3 rounded-full shadow-md transform transition-transform duration-200 
                                    ${shayari.is_published ? 'translate-x-4' : 'translate-x-0'}`} />
                            </div>
                        </label>

                        <button
                            onClick={() => onEdit(shayari)}
                            className="p-2 text-[--primary-btn-color] hover:bg-[--primary-btn-color] hover:text-white rounded-full transition-colors"
                            title="Edit"
                        >
                            <FaPencil />
                        </button>
                        <button
                            onClick={() => onDelete(shayari._id || shayari.no)}
                            className="p-2 text-red-500 hover:bg-red-500 hover:text-white rounded-full transition-colors"
                            title="Delete"
                        >
                            <FaTrash />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// Updated Order Management Modal Component with Swap Functionality
const OrderManagementModal = ({ isOpen, onClose, shayaris, poetName, onReorder }) => {
    const [orderedShayaris, setOrderedShayaris] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [swapWithIndex, setSwapWithIndex] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize orderedShayaris when modal opens or shayaris change
    useEffect(() => {
        if (shayaris && shayaris.length > 0) {
            // Sort by order_no if available, otherwise by no
            const sorted = [...shayaris].sort((a, b) => {
                const orderA = a.order_no !== undefined ? a.order_no : a.no;
                const orderB = b.order_no !== undefined ? b.order_no : b.no;
                return orderA - orderB;
            });
            setOrderedShayaris(sorted);
        }
    }, [shayaris, isOpen]); // Re-initialize when modal opens

    const moveUp = (index) => {
        if (index > 0) {
            const newOrder = [...orderedShayaris];
            [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
            setOrderedShayaris(newOrder);
            setSelectedIndex(index - 1);
            setSwapWithIndex(null);
        }
    };

    const moveDown = (index) => {
        if (index < orderedShayaris.length - 1) {
            const newOrder = [...orderedShayaris];
            [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
            setOrderedShayaris(newOrder);
            setSelectedIndex(index + 1);
            setSwapWithIndex(null);
        }
    };

    const moveToTop = (index) => {
        if (index > 0) {
            const newOrder = [...orderedShayaris];
            const [item] = newOrder.splice(index, 1);
            newOrder.unshift(item);
            setOrderedShayaris(newOrder);
            setSelectedIndex(0);
            setSwapWithIndex(null);
        }
    };

    const moveToBottom = (index) => {
        if (index < orderedShayaris.length - 1) {
            const newOrder = [...orderedShayaris];
            const [item] = newOrder.splice(index, 1);
            newOrder.push(item);
            setOrderedShayaris(newOrder);
            setSelectedIndex(orderedShayaris.length - 1);
            setSwapWithIndex(null);
        }
    };

    const handleSwap = (index1, index2) => {
        if (index1 === index2) return;
        const newOrder = [...orderedShayaris];
        [newOrder[index1], newOrder[index2]] = [newOrder[index2], newOrder[index1]];
        setOrderedShayaris(newOrder);
        setSelectedIndex(index2);
        setSwapWithIndex(null);
    };

    const handleItemClick = (index) => {
        if (swapWithIndex === null) {
            setSwapWithIndex(index);
            setSelectedIndex(index);
        } else {
            if (swapWithIndex !== index) {
                handleSwap(swapWithIndex, index);
            }
            setSwapWithIndex(null);
        }
    };

    const clearSwapSelection = () => {
        setSwapWithIndex(null);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const startNo = Math.min(...orderedShayaris.map(item => item.no || 0));

            const payload = orderedShayaris.map((item, idx) => ({
                _id: item._id,
                no: startNo + idx
            }));

            await onReorder(payload);
            onClose();
        } catch (error) {
            console.error('Error saving order:', error);
            alert(error.message || 'Failed to save order. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset to original order
        const originalSorted = [...shayaris].sort((a, b) => {
            const orderA = a.order_no !== undefined ? a.order_no : a.no;
            const orderB = b.order_no !== undefined ? b.order_no : b.no;
            return orderA - orderB;
        });
        setOrderedShayaris(originalSorted);
        setSelectedIndex(null);
        setSwapWithIndex(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                >
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold text-[--text-color]">
                                Manage Order - {poetName}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Total Shayaris: {orderedShayaris.length}
                            </p>
                            {swapWithIndex !== null && (
                                <p className="text-sm text-blue-600 mt-1">
                                    Selected item #{orderedShayaris[swapWithIndex]?.no}. Click on another item to swap positions.
                                    <button
                                        onClick={clearSwapSelection}
                                        className="ml-2 text-red-500 hover:text-red-700 underline"
                                    >
                                        Cancel
                                    </button>
                                </p>
                            )}
                        </div>
                        <button
                            onClick={handleCancel}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <FaTimes className="text-gray-500 text-xl" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="space-y-3">
                            {orderedShayaris.map((shayari, index) => (
                                <motion.div
                                    key={shayari._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`bg-white border rounded-lg p-4 transition-all duration-200 cursor-pointer ${selectedIndex === index
                                        ? 'border-[--border-color-g] shadow-lg'
                                        : 'border-gray-200 hover:shadow-md'
                                        } ${swapWithIndex === index
                                            ? 'ring-1 ring-[--border-color-g] bg-blue-50'
                                            : ''
                                        }`}
                                    onClick={() => handleItemClick(index)}
                                >
                                    <div className="flex items-start gap-4">
                                        {/* Order Number - Show Database NO */}
                                        <div className="flex-shrink-0 w-24 text-center">
                                            <div>
                                                <span className={`inline-block rounded-lg px-3 py-1 text-sm font-semibold ${swapWithIndex === index
                                                    ? 'bg-[--primary-color] text-white'
                                                    : 'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    No: {shayari.no}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">
                                                New Order: {index + 1}
                                            </p>
                                        </div>

                                        {/* Content Preview - Middle */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-end mb-2">
                                                <p
                                                    className="text-base text-[--text-color-g] break-words w-full"
                                                    id='primary-urdu-fonts'
                                                    dir='rtl'
                                                    dangerouslySetInnerHTML={{
                                                        __html: (shayari.urdu?.substring(0, 100) || '') + (shayari.urdu?.length > 100 ? '...' : '')
                                                    }}
                                                />
                                            </div>
                                            {shayari.devnagari && (
                                                <div className="mt-2 text-sm text-gray-600 break-words">
                                                    {shayari.devnagari.substring(0, 80)}
                                                    {shayari.devnagari.length > 80 && '...'}
                                                </div>
                                            )}
                                        </div>

                                        {/* Action Buttons - Right Side */}
                                        <div className="flex-shrink-0">
                                            <div className="flex gap-1 mb-2 pb-2 border-b border-gray-100">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveToTop(index);
                                                    }}
                                                    disabled={index === 0}
                                                    className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${index === 0
                                                        ? 'text-gray-300 cursor-not-allowed'
                                                        : 'text-[--text-color-g] hover:bg-gray-100'
                                                        }`}
                                                    title="Move to First"
                                                >
                                                    <FaArrowUp className="text-xs" /> First
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveToBottom(index);
                                                    }}
                                                    disabled={index === orderedShayaris.length - 1}
                                                    className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${index === orderedShayaris.length - 1
                                                        ? 'text-gray-300 cursor-not-allowed'
                                                        : 'text-[--text-color-g] hover:bg-gray-100'
                                                        }`}
                                                    title="Move to Last"
                                                >
                                                    <FaArrowDown className="text-xs" /> Last
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (swapWithIndex !== null && swapWithIndex !== index) {
                                                            handleSwap(swapWithIndex, index);
                                                            setSwapWithIndex(null);
                                                        } else if (swapWithIndex === null) {
                                                            setSwapWithIndex(index);
                                                            setSelectedIndex(index);
                                                        } else if (swapWithIndex === index) {
                                                            setSwapWithIndex(null);
                                                        }
                                                    }}
                                                    className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${swapWithIndex === index
                                                        ? 'bg-[--primary-color] text-white'
                                                        : 'text-[--text-color-g] hover:bg-gray-100'
                                                        }`}
                                                    title="Swap with another shayari"
                                                >
                                                    ⇄ Swap
                                                </button>
                                            </div>

                                            <div className="flex gap-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveUp(index);
                                                    }}
                                                    disabled={index === 0}
                                                    className={`flex-1 px-3 py-1.5 rounded text-sm transition-colors flex items-center justify-center gap-1 ${index === 0
                                                        ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                                        : 'text-[--text-color-g]'
                                                        }`}
                                                    title="Move Up"
                                                >
                                                    <FaArrowUp size={12} /> Up
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveDown(index);
                                                    }}
                                                    disabled={index === orderedShayaris.length - 1}
                                                    className={`flex-1 px-3 py-1.5 rounded text-sm transition-colors flex items-center justify-center gap-1 ${index === orderedShayaris.length - 1
                                                        ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                                        : 'text-[--text-color-g]'
                                                        }`}
                                                    title="Move Down"
                                                >
                                                    <FaArrowDown size={12} /> Down
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                        <button
                            onClick={handleCancel}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition-colors disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save Order'}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};


const Shayari = () => {
    const [shayaris, setShayaris] = useState([]);
    const [shayars, setShayars] = useState([]);
    const [formData, setFormData] = useState({
        id: 0,
        poet_id: '',
        poet: '',
        urdu: '',
        roman: '',
        devnagari: '',
        poet_hindi: '',
        poet_urdu: '',
        no: 0,
        is_active: true
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedShayar, setSelectedShayar] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isReordering, setIsReordering] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [pendingOrder, setPendingOrder] = useState([]);
    const [hasOrderChanges, setHasOrderChanges] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchShayari();
        fetchShayar();
    }, []);

    const fetchShayar = async () => {
        try {
            const res = await ApiService.Shayar();
            const formattedShayars = res.data.responseContent.map(shayar => ({
                value: shayar.id,
                label: shayar.name,
                name: shayar.name,
                poet_hindi: shayar.hindi,
                poet_urdu: shayar.urdu,
                _id: shayar._id
            }));
            setShayars(formattedShayars);
        } catch (error) {
            console.log(error);
        }
    };

    const fetchShayari = async () => {
        try {
            setIsLoading(true);
            const res = await ApiService.Shayari();
            console.log("Fetched Shayari:", res);

            if (res.data && res.data.responseContent) {
                const shayarisData = res.data.responseContent;

                // Process shayaris to ensure they have proper order within each poet
                const processedShayaris = [];

                // Group by poet
                const poets = [...new Set(shayarisData.map(item => item.poet).filter(Boolean))];

                poets.forEach(poet => {
                    // Get all shayaris for this poet and sort by 'no'
                    const poetShayaris = shayarisData
                        .filter(item => item.poet === poet)
                        .sort((a, b) => (a.no || 0) - (b.no || 0));

                    // Add to processed list with proper order
                    poetShayaris.forEach((shayari, idx) => {
                        processedShayaris.push({
                            ...shayari,
                            display_order: shayari.no || idx + 1,
                            poet_name: poet
                        });
                    });
                });

                // Add remaining shayaris without poets (if any)
                const remainingShayaris = shayarisData
                    .filter(item => !item.poet)
                    .sort((a, b) => (a.no || 0) - (b.no || 0))
                    .map((item, idx) => ({
                        ...item,
                        display_order: item.no || idx + 1,
                        poet_name: 'Unassigned'
                    }));

                setShayaris([...processedShayaris, ...remainingShayaris]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle drag start
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            setActiveId(null);
            return;
        }

        const activeItem = filteredShayaris.find(
            item => (item._id || item.no.toString()) === active.id
        );

        const overItem = filteredShayaris.find(
            item => (item._id || item.no.toString()) === over.id
        );

        if (!activeItem || !overItem || activeItem.poet !== overItem.poet) {
            setActiveId(null);
            return;
        }

        const currentShayaris = shayaris
            .filter(shayari => shayari.poet === selectedShayar)
            .sort((a, b) => a.no - b.no);

        const oldIndex = currentShayaris.findIndex(
            item => (item._id || item.no.toString()) === active.id
        );

        const newIndex = currentShayaris.findIndex(
            item => (item._id || item.no.toString()) === over.id
        );

        const reordered = arrayMove(currentShayaris, oldIndex, newIndex);

        const startNo = Math.min(...currentShayaris.map(item => item.no));

        const updated = reordered.map((item, idx) => ({
            ...item,
            no: startNo + idx
        }));

        // update UI immediately
        const updatedAll = shayaris.map(shayari => {
            if (shayari.poet === selectedShayar) {
                const found = updated.find(u => u._id === shayari._id);
                return found || shayari;
            }
            return shayari;
        });

        setShayaris(updatedAll);

        // store pending order
        const payload = updated.map(item => ({
            _id: item._id,
            no: item.no
        }));

        setPendingOrder(payload);
        setHasOrderChanges(true);

        setActiveId(null);
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    const handleToggleStatus = async (shayariId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await ApiService.ToggleShayari(shayariId);

            setShayaris(prev =>
                prev.map(shayari =>
                    (shayari._id === shayariId || shayari.no === shayariId)
                        ? { ...shayari, is_published: newStatus }
                        : shayari
                )
            );

            console.log(`Shayari ${newStatus ? 'activated' : 'deactivated'} successfully`);
        } catch (error) {
            console.error('Error updating shayari status:', error);
        }
    };

    const handleShayarChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            poet_id: selectedOption?.value || '',
            poet: selectedOption?.name || '',
            poet_hindi: selectedOption?.poet_hindi || '',
            poet_urdu: selectedOption?.poet_urdu || '',
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    // Get filtered shayaris based on selected poet or search term
    const getFilteredShayaris = () => {
        if (selectedShayar) {
            // Only show shayaris for the selected poet, sorted by 'no'
            return shayaris
                .filter(shayari => shayari.poet === selectedShayar)
                .sort((a, b) => (a.no || 0) - (b.no || 0));
        } else {
            // Search across all shayaris
            return shayaris.filter(shayari =>
                (shayari.poet && shayari.poet.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (shayari.urdu && shayari.urdu.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (shayari.devnagari && shayari.devnagari.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (shayari.roman && shayari.roman.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
    };

    const filteredShayaris = getFilteredShayaris();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return; // prevent multiple clicks
        setIsSubmitting(true);

        try {
            if (!formData.poet_id) {
                alert('Please select a Shayar');
                setIsSubmitting(false);
                return;
            }

            if (!formData.urdu) {
                alert('Urdu text is required');
                setIsSubmitting(false);
                return;
            }

            if (!formData.devnagari && !formData.roman) {
                alert('Please provide either Devnagari or Roman text');
                setIsSubmitting(false);
                return;
            }

            let nextNumber = formData.no;
            if (!formData.id) {
                const poetShayaris = shayaris.filter(s => s.poet === formData.poet);
                nextNumber = poetShayaris.length + 1;
            }

            const payload = {
                poet_id: formData.poet_id,
                urdu: formData.urdu,
                roman: formData.roman || null,
                devnagari: formData.devnagari || null,
                no: nextNumber,
                poet_urdu: formData.poet_urdu || null,
                poet_hindi: formData.poet_hindi || null,
                poet: formData.poet || null,
                is_active: formData.is_active !== false
            };

            let res;

            if (formData.id) {
                res = await ApiService.UpdateShayari(formData.id, payload);
            } else {
                res = await ApiService.AddShayari(payload);
            }

            if (res.data && res.data.success) {
                await fetchShayari();
                setIsModalOpen(false);
                resetForm();
            } else {
                alert(res.data?.message || 'Update failed');
            }

        } catch (error) {
            console.error('Error:', error);
            alert(error.response?.data?.message || 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            id: null,
            poet_id: '',
            poet: '',
            urdu: '',
            poet_urdu: '',
            roman: '',
            devnagari: '',
            no: null,
            is_active: true
        });
    };

    const handleEdit = (shayari) => {
        const selectedShayarObj = shayars.find(s => s.name === shayari.poet);

        setFormData({
            id: shayari._id || shayari.no,
            poet_id: selectedShayarObj?.value || '',
            poet: shayari.poet || '',
            urdu: shayari.urdu || '',
            roman: shayari.roman || '',
            poet_urdu: shayari.poet_urdu || '',
            devnagari: shayari.devnagari || '',
            no: shayari.no || null,
            is_active: shayari.is_active !== false
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this Shaa'irii?");
        if (!confirmDelete) return;

        try {
            await ApiService.DeleteShayari(id);

            // After deletion, renumber the remaining shayaris for that poet
            const shayariToDelete = shayaris.find(s => s._id === id || s.no === id);
            if (shayariToDelete && shayariToDelete.poet) {
                const poetShayaris = shayaris
                    .filter(s => s.poet === shayariToDelete.poet)
                    .sort((a, b) => (a.no || 0) - (b.no || 0));

                // Update numbers for remaining shayaris
                const renumberData = poetShayaris
                    .filter(s => (s._id !== id && s.no !== id))
                    .map((item, idx) => ({
                        _id: item._id,
                        no: idx + 1
                    }));

                if (renumberData.length > 0) {
                    await ApiService.UpdateShayariNo(renumberData);
                }
            }

            await fetchShayari(); // Refresh to update remaining orders
        } catch (error) {
            console.error('Error deleting shayari:', error);
        }
    };

    const handleOpenOrderModal = () => {
        if (selectedShayar && filteredShayaris.length > 0) {
            setIsOrderModalOpen(true);
        }
    };

    const handleReorderFromModal = async (orderPayload) => {
        try {
            setIsReordering(true);

            console.log("Received order payload:", orderPayload);

            // Call API to update order
            const response = await ApiService.UpdateShayariNo(orderPayload);
            await fetchShayari();
            console.log("API response:", response);

            // Update local state with new order numbers
            const updatedShayaris = shayaris.map(shayari => {
                const updatedOrder = orderPayload.find(p => p._id === shayari._id);
                if (updatedOrder && shayari.poet === selectedShayar) {
                    return { ...shayari, no: updatedOrder.no };
                }
                return shayari;
            });

            // Update the main shayaris state
            setShayaris(updatedShayaris);

            // Clear pending changes
            setHasOrderChanges(false);
            setPendingOrder([]);

            console.log("Order updated successfully from modal");

            // Optional: Show success message
            // alert("Order updated successfully!");

        } catch (error) {
            console.error("Order update failed:", error);
            console.error("Error details:", error.response?.data);
            throw new Error(error.response?.data?.message || "Failed to update order");
        } finally {
            setIsReordering(false);
        }
    };

    const handleConfirmOrder = async () => {
        if (!pendingOrder.length) return;

        try {
            setIsReordering(true);
            await ApiService.UpdateShayariNo(pendingOrder);
            setHasOrderChanges(false);
            setPendingOrder([]);
            console.log("Order updated successfully");
        } catch (error) {
            console.error("Order update failed:", error);
            alert("Failed to update order");
            fetchShayari();
        } finally {
            setIsReordering(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[--primary-color]"></div>
            </div>
        );
    }

    return (
        <div className="mx-auto p-4" id='primary-fonts'>
            <h1 className="text-3xl font-bold mb-8 text-[--text-color] flex items-center gap-2">
                <FaBook className="text-[--primary-color]" /> Shaa'irii Collection
            </h1>

            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div className="relative w-full md:w-96">
                    {selectedShayar ? (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setSelectedShayar(null)}
                                className="flex items-center gap-2 text-[--text-color-g] px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                <FaChevronLeft /> Back to all shaa'ir
                            </button>
                            <button
                                onClick={handleOpenOrderModal}
                                className="flex items-center gap-2 text-white bg-[--primary-btn-color] px-4 py-2 rounded-lg hover:bg-[--secondary-btn-color] transition-colors"
                                title="Manage Order"
                            >
                                <FaList /> Manage Order
                            </button>
                        </div>
                    ) : (
                        <>
                            <input
                                type="text"
                                placeholder="Search shaa'irii by content or poet..."
                                className="w-full pl-10 pr-4 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                        </>
                    )}
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setIsModalOpen(true);
                    }}
                    className="w-full md:w-auto bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition duration-200 shadow-md"
                >
                    <FaPlus /> Add Shaa'irii
                </button>
            </div>

            {!selectedShayar ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {[...new Set(
                        shayaris
                            .filter(shayari =>
                                shayari.poet && shayari.poet.toLowerCase().includes(searchTerm.toLowerCase())
                            )
                            .map(shayari => shayari.poet)
                    )].filter(Boolean).map((shayar) => {
                        const poetShayaris = shayaris.filter(s => s.poet === shayar);
                        return (
                            <div
                                key={shayar}
                                onClick={() => setSelectedShayar(shayar)}
                                className="bg-white p-6 rounded-lg shadow-md border border-[--border-color-g] hover:shadow-lg transition duration-300 cursor-pointer text-center"
                            >
                                <div className="text-xl font-semibold text-[--text-color-g]">{shayar}</div>
                                <div className="text-sm text-gray-500 mt-1">
                                    {poetShayaris.length} shaa'irii
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div>
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-[--text-color] flex items-center gap-2">
                            <span className="text-[--text-color-g]">{selectedShayar}</span>
                        </h2>
                        {isReordering && (
                            <span className="text-sm text-blue-500 animate-pulse flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                                Updating order...
                            </span>
                        )}
                        {hasOrderChanges && (
                            <button
                                onClick={handleConfirmOrder}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Confirm Order
                            </button>
                        )}
                    </div>

                    {filteredShayaris.length > 0 ? (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}
                        >
                            <SortableContext
                                items={filteredShayaris.map(s => s._id || s.no.toString())}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filteredShayaris.map((shayari) => (
                                        <SortableShayariItem
                                            key={shayari._id || shayari.no}
                                            shayari={shayari}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onToggleStatus={handleToggleStatus}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-lg text-gray-600">No shayaris found for this poet</p>
                        </div>
                    )}
                </div>
            )}

            {/* Order Management Modal */}
            <OrderManagementModal
                isOpen={isOrderModalOpen}
                onClose={() => setIsOrderModalOpen(false)}
                shayaris={filteredShayaris}
                poetName={selectedShayar}
                onReorder={handleReorderFromModal}
            />

            {/* Add/Edit Shayari Modal */}
            {isModalOpen && (
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="bg-white rounded-lg shadow-xl w-full max-w-screen-md max-h-[90vh] overflow-y-auto"
                        >
                            <div className="p-6">
                                <h2 className="text-xl font-semibold text-[--text-color] mb-4">
                                    {formData.id ? 'Edit Shaa\'irii' : 'Add New Shaa\'irii'}
                                </h2>
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-4">
                                        <label className="block text-[--text-color] font-bold mb-2" htmlFor="poet">
                                            Shaa'ir Name
                                        </label>
                                        <Select
                                            id="poet"
                                            name="poet"
                                            options={shayars}
                                            value={shayars.find(option => option.value === formData.poet_id)}
                                            onChange={handleShayarChange}
                                            placeholder="Select a Shaa'ir"
                                            className="w-full"
                                            classNamePrefix="react-select"
                                            isClearable
                                            required
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label className="block text-[--text-color] font-bold mb-2" htmlFor="urdu">
                                            Urdu
                                        </label>
                                        <textarea
                                            id="primary-urdu-fonts"
                                            name="urdu"
                                            value={formData.urdu}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg"
                                            rows="3"
                                            dir='rtl'
                                            required
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label className="block text-[--text-color] font-bold mb-2" htmlFor="devnagari">
                                            Devnagari
                                        </label>
                                        <textarea
                                            id="devnagari"
                                            name="devnagari"
                                            value={formData.devnagari}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg"
                                            rows="3"
                                            required
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label className="block text-[--text-color] font-bold mb-2" htmlFor="roman">
                                            Roman
                                        </label>
                                        <textarea
                                            id="roman"
                                            name="roman"
                                            value={formData.roman}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg"
                                            rows="3"
                                        />
                                    </div>
                                    <div className="flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            disabled={isSubmitting}
                                            onClick={() => {
                                                setIsModalOpen(false);
                                                resetForm();
                                            }}
                                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isSubmitting ? "Saving..." : formData.id ? "Update" : "Save"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </motion.div>
                </AnimatePresence>
            )}
        </div>
    );
};

export default Shayari;


// if we can drag and drop with drag to reorder button the work well
// but we can change from model why not working well
// they change different no


// if we select a shayar and open manage order model
// if  we we set a reordering of shayari why and click on Save Order button why order are not set and  data are not adjust in model